import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './TenantSelectionPage.css';

// API 기본 URL 설정
const API_URL = process.env.REACT_APP_API_URL || '/api';

const TenantSelectionPage = () => {
  const navigate = useNavigate();
  const [selectedTenant, setSelectedTenant] = useState('');
  const [availableTenants, setAvailableTenants] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null); // 사용자 정보 추가
  const [isSuperAdmin, setIsSuperAdmin] = useState(false); // 슈퍼관리자 권한 확인
  
  // 테넌트 선택 처리 (useCallback으로 먼저 정의)
  const handleTenantSelect = useCallback(async (tenantId) => {
    try {
      setError('');
      setSelectedTenant(tenantId);
      
      console.log(`테넌트 선택 시작: ${tenantId}`);
      
      // 테넌트 선택 API 호출
      const response = await axios.post(
        `${API_URL}/users/select-tenant/`,
        { tenant_name: tenantId },
        { 
          withCredentials: true, // 쿠키를 포함하여 요청 및 받기
          // 명시적으로 Content-Type 헤더 설정
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('테넌트 선택 응답:', response.status, response.data);
      
      if (response.status === 200) {
        // 응답 헤더 확인 (디버깅용)
        console.log('응답 헤더:', response.headers);
        
        // 테넌트 선택 성공 - 세션 스토리지에 저장
        sessionStorage.setItem('current_tenant', tenantId);
        
        // 백엔드에서 마지막 접근 대시보드를 관리하므로 dashboard_id 저장 불필요
        
        // 쿠키가 설정될 시간을 주기 위해 약간의 지연 후 리다이렉트
        setTimeout(() => {
          console.log(`테넌트 선택 완료, 홈으로 이동합니다: ${tenantId}`);
          // 페이지 강제 리프레시로 리다이렉트
          window.location.href = '/';
        }, 300);
      } else {
        setError('테넌트 선택 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('테넌트 선택 오류:', error);
      setError('테넌트 선택 중 오류가 발생했습니다.');
    }
  }, []);
  
  // 페이지 로드 시 사용자 정보와 테넌트 목록 가져오기
  useEffect(() => {
    const fetchUserInfoAndTenants = async () => {
      let shouldShowPage = true; // 페이지를 보여줄지 여부

      try {
        // 사용자 정보 및 권한 확인
        const userResponse = await axios.get(`${API_URL}/users/me/`, {
          withCredentials: true
        });

        console.log('테넌트 선택 페이지 - 사용자 정보:', userResponse.data);

        const isSuper = !!userResponse.data?.is_superuser;

        if (userResponse.data) {
          setUserInfo(userResponse.data);
          setIsSuperAdmin(isSuper);
          console.log('테넌트 선택 페이지 - 슈퍼관리자 권한:', isSuper);
        }

        // 테넌트 목록 가져오기
        const response = await axios.get(`${API_URL}/users/tenants/`, {
          withCredentials: true // 쿠키를 포함하여 요청
        });

        if (response.data.tenants && response.data.tenants.length > 0) {
          setAvailableTenants(response.data.tenants);

          // 기본 테넌트가 있으면 선택, 없으면 첫 번째 테넌트 선택
          const defaultTenant = response.data.tenants.find(t => t.is_default);
          if (defaultTenant) {
            setSelectedTenant(defaultTenant.tenant_name);
          } else {
            setSelectedTenant(response.data.tenants[0].tenant_name); // Fallback to the first tenant
          }

          // 테넌트가 하나만 있고 슈퍼관리자가 아닌 경우 자동으로 선택
          if (response.data.tenants.length === 1 && !isSuper) {
            console.log('테넌트가 1개만 존재하여 자동 선택:', response.data.tenants[0].tenant_name);
            shouldShowPage = false; // 자동 선택 시 페이지 표시하지 않음
            handleTenantSelect(response.data.tenants[0].tenant_name);
            return;
          }
        } else {
          setError('접근 가능한 테넌트가 없습니다.');
        }

      } catch (error) {
        console.error('사용자 정보 또는 테넌트 목록 조회 오류:', error);
        // Handle specific errors like 401 if needed
        if (error.response?.status === 401) {
             setError('인증되지 않았습니다. 로그인이 필요합니다.');
             // Optionally redirect to login
             // navigate('/login');
        } else {
             setError('데이터를 불러오는 중 오류가 발생했습니다.');
        }
      } finally {
        // 자동 선택이 아닌 경우에만 로딩 해제
        if (shouldShowPage) {
          setLoading(false);
        }
      }
    };
    
    fetchUserInfoAndTenants();
  }, [handleTenantSelect]); // handleTenantSelect를 dependency에 추가

  // 관리자 페이지로 직접 이동
  const handleAdminPageSelect = () => {
    console.log('관리자 페이지로 이동');
    // 테넌트 선택 없이 바로 관리자 페이지로 이동
    navigate('/admin');
  };

  if (loading) {
    return <div className="tenant-selection-container">
      <div className="tenant-selection-box">
        <p>데이터를 불러오는 중...</p>
      </div>
    </div>;
  }

  return (
    <div className="tenant-selection-container">
      <div className="tenant-selection-box">
        <h1 className="tenant-selection-title">서비스 선택</h1>
        <p className="tenant-selection-subtitle">
          사용하실 서비스를 선택해주세요
        </p>
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="tenant-cards">
          {/* 기존 테넌트 카드들 */}
          {availableTenants.map((tenant) => (
            <div 
              key={tenant.tenant_name}
              className={`tenant-card ${selectedTenant === tenant.tenant_name ? 'selected' : ''}`}
              onClick={() => handleTenantSelect(tenant.tenant_name)}
            >
              <div className="tenant-icon">
                {tenant.tenant_name === 'ess' ? (
                  <span role="img" aria-label="battery">🔋</span>
                ) : (
                  <span role="img" aria-label="car">🚗</span>
                )}
              </div>
              <h3 className="tenant-name">
                {tenant.tenant_name === 'ess' ? 'ESS' : 
                 tenant.tenant_name === 'evcheck' ? 'EVCheck' : 
                 tenant.tenant_name}
              </h3>
              <p className="tenant-description">
                {tenant.tenant_name === 'ess' ? 'Energy Storage System 모니터링 및 분석' : 
                 tenant.tenant_name === 'evcheck' ? '전기차 배터리 진단 및 분석' : 
                 '서비스'}
                {tenant.is_default && ' (기본)'}
              </p>
            </div>
          ))}
          
          {/* 슈퍼관리자일 때만 관리자 카드 표시 */}
          {isSuperAdmin && (
            <div 
              className="tenant-card admin-card"
              onClick={handleAdminPageSelect}
            >
              <div className="tenant-icon admin-icon">
                <span role="img" aria-label="admin">🔧</span>
              </div>
              <h3 className="tenant-name">관리자</h3>
              <p className="tenant-description">
                시스템 관리 및 사용자 관리
                {' (슈퍼 관리자)'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantSelectionPage; 